import React from 'react';
import { Text, Select, Flex, Checkbox } from '@scale/scaleui-radix';
import { VellumContext } from '@scale/genai-compass-shared/cards/vellum';
import { VellumProductsResponse } from '@scale/shared/types/vellum';

interface VellumMenuProps {
  context: VellumContext;
  products: VellumProductsResponse;
  selectedProduct: any;
  onProductChange: (productId: string) => void;
  onOptionChange: (optionName: string, valueId: string) => void;
  onConfirmationChange: (checked: boolean) => void;
}

const VellumMenu: React.FC<VellumMenuProps> = ({
  context,
  products,
  selectedProduct,
  onProductChange,
  onOptionChange,
  onConfirmationChange,
}) => {
  return (
    <div className="w-1/3 flex flex-col gap-4">
      {/* Product Selection */}
      <div>
        <Text as="p" size="2" className="font-medium mb-3">
          Menu Selection
        </Text>

        <div className="mb-3">
          <Text as="p" size="2" className="text-gray-700 mb-2">
            Select a Drink (Required)
          </Text>
          <Select.Root value={context.productId?.toString() || ''} onValueChange={onProductChange}>
            <Select.Trigger placeholder="Choose a drink..." className="w-full" />
            <Select.Content>
              {products?.categories.map(category => (
                <Select.Group key={category.id}>
                  <Select.Label>{category.name}</Select.Label>
                  {category.products.map(product => (
                    <Select.Item key={product.id} value={product.id.toString()}>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          {product.name}
                        </Text>
                        {product.description && product.description.trim() !== '' && (
                          <Text size="1" color="gray">
                            {product.description}
                          </Text>
                        )}
                        {product.price != null && product.price > 0 && (
                          <Text size="1" weight="medium">
                            ${product.price.toFixed(2)}
                          </Text>
                        )}
                      </Flex>
                    </Select.Item>
                  ))}
                </Select.Group>
              ))}
            </Select.Content>
          </Select.Root>
        </div>

        {/* Product Options */}
        {selectedProduct && selectedProduct.options.length > 0 && (
          <div className="mt-4">
            <Text as="p" size="2" className="font-medium mb-3">
              Customize Your Order
            </Text>

            {selectedProduct.options.map((option: any) => (
              <div key={option.id} className="mb-3">
                <Text as="p" size="2" className="text-gray-700 mb-2">
                  {option.name} {option.required && <span className="text-red-500">*</span>}
                </Text>
                <Select.Root
                  value={
                    context.optionSelections?.[option.name] ||
                    (option.required ? undefined : 'none')
                  }
                  onValueChange={value => onOptionChange(option.name, value)}
                >
                  <Select.Trigger
                    placeholder={
                      option.required ? 'Required selection...' : 'Optional selection...'
                    }
                    className="w-full"
                  />
                  <Select.Content>
                    {!option.required && <Select.Item value="none">No selection</Select.Item>}
                    {option.values.map((value: any) => (
                      <Select.Item key={value.id} value={value.id.toString()}>
                        <Flex justify="between" align="center" width="100%">
                          <Text size="2">{value.name}</Text>
                          {value.price != null && value.price > 0 && (
                            <Text size="1" color="gray">
                              +${value.price.toFixed(2)}
                            </Text>
                          )}
                        </Flex>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Summary */}
      {selectedProduct && (
        <div className="mt-4">
          <Text as="p" size="2" className="font-medium mb-3">
            Order Summary
          </Text>

          <div className="space-y-2">
            <Flex justify="between">
              <Text size="2">Product:</Text>
              <Text size="2" weight="medium">
                {selectedProduct.name}
              </Text>
            </Flex>

            {Object.entries(context.optionSelections || {}).map(([optionName, valueId]) => {
              const option = selectedProduct.options.find((opt: any) => opt.name === optionName);
              const value = option?.values.find((val: any) => val.id.toString() === valueId);

              if (!value) return null;

              return (
                <Flex key={optionName} justify="between">
                  <Text size="2">{optionName}:</Text>
                  <Text size="2">{value.name}</Text>
                </Flex>
              );
            })}
          </div>
        </div>
      )}

      {/* Office Confirmation */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <Flex direction="row" gap="3" align="start">
          <Checkbox
            checked={context.sfOfficeConfirmation || false}
            onCheckedChange={onConfirmationChange}
            className="mt-0.5"
          />
          <div className="flex-1">
            <Text
              as="p"
              size="2"
              className="text-gray-900 cursor-pointer select-none"
              onClick={() => onConfirmationChange(!context.sfOfficeConfirmation)}
            >
              I confirm that the list of employees is in the SF office at 650 Townsend
            </Text>
            <Text as="p" size="1" className="text-red-600 mt-1">
              *Required for order submission
            </Text>
          </div>
        </Flex>
      </div>
    </div>
  );
};

export default VellumMenu;
